// frontend/src/components/ScenariosMapViewResults.jsx

import React, { useRef, useEffect, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTheme } from '@mui/material/styles';
import { authFetch } from '../authFetch';
import { icons, highlightedIcons } from "./icons";
import API_BASE_URL from '../config';

const ScenariosMapViewResults = ({ scenarioId }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [criticalFeatures, setCriticalFeatures] = useState(null); // null means "loading"
  const [hazardGeoJson, setHazardGeoJson] = useState(null);       // null means "loading"
  const [hazardBounds, setHazardBounds] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const theme = useTheme();

  // 1. Fetch Data
  useEffect(() => {
    if (scenarioId) {
      setCriticalFeatures(null);
      setHazardGeoJson(null);
      setHazardBounds(null);

      // Fetch critical features using RELATIVE path for authFetch
      authFetch(`/api/scenarios/${scenarioId}/critical_features/`)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.json();
        })
        .then(data => setCriticalFeatures(data || []))
        .catch(err => {
          console.error(`Error fetching critical features:`, err);
          setCriticalFeatures([]); // Fallback to empty array so map still loads
        });

      // Fetch hazard GeoJSON using RELATIVE path for authFetch
      authFetch(`/api/scenarios/${scenarioId}/hazard_geojson/`)
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (data && data.geojson && data.bounds) {
            setHazardGeoJson(data.geojson);
            setHazardBounds(data.bounds);
          } else {
            setHazardGeoJson({}); // Mark as loaded but empty (e.g., Baseline scenario)
          }
        })
        .catch(err => {
          console.warn(`Hazard GeoJSON not found (expected if baseline scenario):`, err);
          setHazardGeoJson({}); // Mark as loaded but empty
        });
    }
  }, [scenarioId]);

  // 2. Handle Zooming to Bounds
  useEffect(() => {
    if (map.current && hazardBounds) {
      try {
        map.current.fitBounds(hazardBounds, {
          padding: 40,
          duration: 1000
        });
      } catch (e) {
        console.warn("Could not fit bounds:", e);
      }
    }
  }, [hazardBounds]);

  // 3. Helper to load icons
  const addIconToMap = useCallback((iconUrl, iconId) => {
    if (!map.current) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
      if (map.current.hasImage(iconId)) {
        resolve(); 
        return;
      }

      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = iconUrl;
      img.onload = () => {
        if (!map.current) { resolve(); return; }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        context.drawImage(img, 0, 0);
        const pngUrl = canvas.toDataURL('image/png');

        if (map.current) {
          map.current.loadImage(pngUrl, (error, image) => {
            if (error) {
              console.error(`Error loading image ${iconId}:`, error);
              resolve(); // Resolve anyway so Promise.allSettled doesn't halt
            } else {
              if (map.current && !map.current.hasImage(iconId)) {
                map.current.addImage(iconId, image);
              }
              resolve();
            }
          });
        } else {
          resolve();
        }
      };
      img.onerror = () => {
        console.warn(`Warning: Could not load icon URL ${iconId}`);
        resolve(); // Resolve to prevent breaking the rendering chain
      };
    });
  }, []);

  // 4. Main Layer Setup Function
  const setupMapLayers = useCallback(async () => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    // Load icons
    const allIcons = { ...icons, ...highlightedIcons };
    const iconPromises = Object.entries(allIcons).map(([name, url]) => addIconToMap(url, name));
    await Promise.allSettled(iconPromises);
    
    if (!map.current) return;

    // --- CLEANUP --- 
    ['critical-nodes', 'combined-network-nodes', 'base-network-lines', 'hazard-fill', 'hazard-outline'].forEach(id => {
      if (map.current.getLayer(id)) map.current.removeLayer(id);
    });
    ['network-tiles', 'hazard-data'].forEach(id => {
      if (map.current.getSource(id)) map.current.removeSource(id);
    });

    // --- SOURCES ---
    
    // A. Network Tiles (Mapbox requires absolute URL if fetching cross-domain)
    map.current.addSource('network-tiles', {
      type: 'vector',
      tiles: [`${API_BASE_URL}/api/tiles/combined_network/{z}/{x}/{y}.pbf`],
      minzoom: 0,
      maxzoom: 12
    });

    // B. Hazard Tiles (Only add if hazard data actually exists, preventing 404s for Baseline)
    const hasHazard = hazardGeoJson && Object.keys(hazardGeoJson).length > 0;
    if (scenarioId && hasHazard) {
      map.current.addSource('hazard-data', {
        type: 'vector',
        tiles: [`${API_BASE_URL}/api/tiles/scenarios/${scenarioId}/hazard_area/{z}/{x}/{y}.pbf`],
        minzoom: 0,
        maxzoom: 12
      });

      const HAZARD_SOURCE_LAYER = 'hazard_area';

      map.current.addLayer({
        id: 'hazard-fill',
        type: 'fill',
        source: 'hazard-data',
        'source-layer': HAZARD_SOURCE_LAYER,
        paint: {
          'fill-color': theme.palette.mode === 'dark' ? '#ff4d4d' : '#ff6666',
          'fill-opacity': 0.4
        }
      });

      map.current.addLayer({
        id: 'hazard-outline',
        type: 'line',
        source: 'hazard-data',
        'source-layer': HAZARD_SOURCE_LAYER,
        paint: {
          'line-color': theme.palette.mode === 'dark' ? '#ff8080' : '#e60000',
          'line-width': 1.5
        }
      });
    }

    // --- LAYERS ---

    // 1. Base Network Lines
    const BASE_NETWORK_SOURCE_LAYER = 'network_with_critical_flags';
    map.current.addLayer({
      id: 'base-network-lines',
      type: 'line',
      source: 'network-tiles',
      'source-layer': BASE_NETWORK_SOURCE_LAYER,
      filter: ['all', ['==', '$type', 'LineString'], ['!=', 'line_type', 'Transmission']],
      paint: {
        'line-color': theme.palette.mode === 'dark' ? '#555' : '#D3D3D3',
        'line-width': 1
      }
    });

    // 2. Base Infrastructure Icons
    const getIconName = (baseName) => theme.palette.mode === 'dark' ? `${baseName}-dark` : baseName;

    map.current.addLayer({
      id: 'combined-network-nodes',
      type: 'symbol',
      source: 'network-tiles',
      'source-layer': BASE_NETWORK_SOURCE_LAYER,
      filter: ['all', ['==', '$type', 'Point'], ['!=', 'name', 'Road']],
      layout: {
        'icon-image': [
          'match', ['get', 'name'],
          'School', getIconName('school'),
          'Port', getIconName('directions-boat'),
          'Harbour', getIconName('directions-boat'),
          'Household', getIconName('home'),
          'Supermarket', getIconName('local-grocery-store'),
          'Warehouse', getIconName('warehouse'),
          'Farm', getIconName('agriculture'),
          'Power station', getIconName('factory'),
          'Power substation', getIconName('power'),
          'Hospital', getIconName('local-hospital'),
          'Clinic', getIconName('local-hospital'),
          'Pharmacy', getIconName('pharmacy'),
          'Fire station', getIconName('fire-station'),
          'Police station', getIconName('police'),
          'Military base', getIconName('police'),
          'Train station', getIconName('train'),
          'Airport', getIconName('plane'),
          'Government office', getIconName('office'),
          'Courthouse', getIconName('office'),
          'City hall', getIconName('office'),
          'Communication tower', getIconName('communication-tower'),
          'Water reservoir', getIconName('reservoir'),
          'Water plant', getIconName('droplet'),
          'Wastewater plant', getIconName('droplet'),
          getIconName('home') // Default fallback
        ],
        'icon-size': 1,
        'icon-allow-overlap': true,
      },
      minzoom: 12,
    });

    // 3. Highlighted Critical Nodes
    if (criticalFeatures && criticalFeatures.length > 0) {
      const criticalCodes = criticalFeatures.map(feature => feature.feature_id);
      
      map.current.addLayer({
        id: 'critical-nodes',
        type: 'symbol',
        source: 'network-tiles',
        'source-layer': BASE_NETWORK_SOURCE_LAYER,
        filter: ['all', ['==', '$type', 'Point'], ['in', 'code', ['literal', criticalCodes]]],
        layout: {
          'icon-image': [
            'match', ['get', 'name'],
            'Road', 'road-highlighted',
            'School', 'school-highlighted',
            'Port', 'directions-boat-highlighted',
            'Harbour', 'directions-boat-highlighted',
            'Household', 'home-highlighted',
            'Supermarket', 'local-grocery-store-highlighted',
            'Warehouse', 'warehouse-highlighted',
            'Farm', 'agriculture-highlighted',
            'Power station', 'factory-highlighted',
            'Power substation', 'power-highlighted',
            'Hospital', 'local-hospital-highlighted',
            'Clinic', 'local-hospital-highlighted',
            'Pharmacy', 'pharmacy-highlighted',
            'Fire station', 'fire-station-highlighted',
            'Police station', 'police-highlighted',
            'Military base', 'police-highlighted',
            'Train station', 'train-highlighted',
            'Airport', 'plane-highlighted',
            'Government office', 'office-highlighted',
            'Courthouse', 'office-highlighted',
            'City hall', 'office-highlighted',
            'Communication tower', 'communication-tower-highlighted',
            'Water reservoir', 'reservoir-highlighted',
            'Water plant', 'droplet-highlighted',
            'Wastewater plant', 'droplet-highlighted',
            'home-highlighted' // Default icon
          ],
          'icon-size': 1.2,
          'icon-allow-overlap': true,
          'text-field': ['get', 'name'],
          'text-offset': [0, 0.5],
          'text-anchor': 'top',
        },
        paint: {
          'text-color': theme.palette.mode === 'dark' ? 'rgba(255, 100, 100, 0.9)' : 'darkred',
          'text-halo-color': theme.palette.mode === 'dark' ? 'rgba(50,0,0,0.7)' : 'rgba(255, 255, 255, 0.8)',
          'text-halo-width': 1.5,
        },
        minzoom: 12,
      });
    }
  }, [scenarioId, criticalFeatures, hazardGeoJson, theme.palette.mode, addIconToMap]);

  // 5. Initialize Map
  useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoibXZwYXNzb3MiLCJhIjoiY2t6em4xcnpwMGJ2bDNicDYyemM0bWU4YyJ9.fR55BgEafulrz4yLfY2oRA';
    if (map.current || !mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: theme.palette.mode === 'dark' ? 'mapbox://styles/mapbox/dark-v10' : 'mapbox://styles/mapbox/light-v10',
      center: [18.0656, 59.3327],
      zoom: 12,
    });

    map.current.on('load', () => setIsMapLoaded(true));

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        setIsMapLoaded(false);
      }
    };
  }, [theme.palette.mode]);

  // 6. Trigger Layer Setup
  useEffect(() => {
    // Only set up layers when fetches have settled (either data or empty objects, but no longer null)
    if (isMapLoaded && scenarioId && criticalFeatures !== null && hazardGeoJson !== null) {
      setupMapLayers();
    }
  }, [isMapLoaded, scenarioId, criticalFeatures, hazardGeoJson, setupMapLayers]);

  return (
    <div ref={mapContainer} style={{ width: '100%', height: 'calc(100vh - 200px)', minHeight: '540px', borderRadius: '8px' }} />
  );
};

export default ScenariosMapViewResults;